import { mainAPIUrl } from '../../../settings';

const baseURL = `${mainAPIUrl}/gservices_3/ss`;

/**
 * Submit delivery form data with nonce verification
 * Endpoint: POST /gservices_3/ss/smartsend/v2/:ssid/complete
 *
 * @param {string} ssid - SmartSend ID (from order.smartSendId)
 * @param {Object} deliveryData - Form submission data
 * @param {string} nonce - X-Secure-Verify token (one-time use)
 * @param {string} token - Authorization token (passed as Bearer token in Authorization header only)
 * @returns {Promise<{success: boolean, data?: any, error?: string, newNonce?: string}>}
 *
 * If submission fails, the response will include a NEW nonce in X-Secure-Verify header
 * that must be used for the retry attempt
 *
 * Security: Token is passed only in the Authorization header as Bearer token to avoid
 * exposing it in server logs, browser history, and referrer headers.
 */
export const submitDeliveryForm = async (ssid, deliveryData, nonce, token) => {
  const url = `${baseURL}/smartsend/v2/${ssid}/complete`;

  try {
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Secure-Verify': nonce, // Send nonce for verification
    };

    // Add authorization header if token is provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(deliveryData),
    });

    const json = await response.json();

    // If request failed, extract new nonce from response headers for retry
    if (!response.ok) {
      const newNonce = response.headers.get('X-Secure-Verify');

      // Normalize field names: backend may return 'phoneNumber' but frontend expects 'phone'
      const normalizedMissing = json.missing?.map((field) => 
        field === 'phoneNumber' ? 'phone' : field
      ) || [];

      return {
        success: false,
        error: json.error || json.message || 'Failed to submit delivery details',
        code: json.code, // Backend response code (e.g., IDENTITY_MISSING, TOKEN_EXPIRED)
        missing: normalizedMissing, // For RECIPIENT_INFO_REQUIRED (normalized field names)
        message: json.message,
        newNonce, // New nonce for retry (if provided by backend)
        data: json,
      };
    }

    // Success - return backend response code and data
    return {
      success: true,
      code: json.code, // Backend success code (should be "SUCCESS")
      data: json.data || json,
      orderId: json.orderId || json.data?.orderId,
    };
  } catch (error) {
    console.error('Submission error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
    };
  }
};
